/**
 * AdminBulkEmailPage — compose an announcement, then review every email before it is sent.
 * Replaces the Django admin page at /admin/email/bulk-email/ (both use email_admin/bulk.py).
 */
import React, { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import {
  Alert, Autocomplete, Box, Button, Card, CardActionArea, Chip, CircularProgress, Container, FormControlLabel,
  MenuItem, Paper, Radio, RadioGroup, Snackbar, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { Campaign as CampaignIcon, MarkEmailRead as ReviewIcon } from '@mui/icons-material';
import { axiosService, fetcher } from 'shared/helpers/axios';
import BackButton from 'components/common/buttons/BackButton';
import EmailOutboxDialog from './email/EmailOutboxDialog';

const TEMPLATES = {
  labs_positions_announcement: {
    icon: '💼', subject: 'Labs for research groups, and open positions on Otable',
    text: "Kemal's update on Labs (invitations, theses, public pages) and the new Positions tab, with a personal note on each person's lab and whether they can post openings.",
  },
  whats_new_announcement: {
    icon: '🔬', subject: 'Labs, funding calls picked for you, and a better Publication Writer',
    text: "Kemal's update on Labs, AI-picked funding calls and the Publication Writer, with a personal note on each person's lab, call emails and manuscripts, and links to the blog.",
  },
  call_announcement: {
    icon: '📢', subject: 'New funding calls that fit your research',
    text: 'Funding call cards, platform links and a call-to-action. Calls can be picked for each recipient from their profile.',
  },
  ai_announcement: {
    icon: '✨', subject: 'Draft proposal sections with the AI Writing Assistant',
    text: 'Introduces the AI Writing Assistant with the free credits on offer and a three-step guide.',
  },
  publication_writer_announcement: {
    icon: '📝', subject: 'Write your next paper together on Otable',
    text: "What's new in the Publication Writer, use cases, and a personal note on each user's own publications or consortia.",
  },
  bulk_email: {
    icon: '📨', subject: '',
    text: 'Free-form message, optionally with funding calls.',
  },
};

const withCalls = (t) => t === 'call_announcement' || t === 'bulk_email';

export default function AdminBulkEmailPage() {
  const { data: meta } = useSWR('/admin/email/options/', fetcher);
  const [template, setTemplate] = useState('labs_positions_announcement');
  const [subject, setSubject] = useState(TEMPLATES.labs_positions_announcement.subject);
  const [message, setMessage] = useState('');
  const [aiCredits, setAiCredits] = useState(2);
  const [callMode, setCallMode] = useState('profile');
  const [callLimit, setCallLimit] = useState(3);
  const [calls, setCalls] = useState([]);
  const [callQuery, setCallQuery] = useState('');
  const [recipientType, setRecipientType] = useState('test');
  const [testEmail, setTestEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [reviewing, setReviewing] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => { if (meta?.sender?.email && !testEmail) setTestEmail(meta.sender.email); }, [meta]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: callResults } = useSWR(
    withCalls(template) && callMode === 'search' ? `/admin/email/calls/?q=${encodeURIComponent(callQuery)}` : null, fetcher,
  );

  const pickTemplate = (key) => {
    // Swap the subject only if it is still the previous template's suggestion
    if (!subject || subject === TEMPLATES[template].subject) setSubject(TEMPLATES[key].subject);
    setTemplate(key);
    setErrors({});
  };

  const options = useMemo(() => ({
    template_type: template, subject, message, ai_credits: aiCredits,
    call_selection_mode: callMode, call_limit: callLimit, selected_call_ids: calls.map((c) => c.id),
    recipient_type: recipientType, test_email: testEmail,
  }), [template, subject, message, aiCredits, callMode, callLimit, calls, recipientType, testEmail]);

  const loadOutbox = async () => {
    try {
      return (await axiosService.post('/admin/email/outbox/', { options })).data;
    } catch (e) {
      if (e.response?.data?.errors) {
        setErrors(e.response.data.errors);
        setReviewing(false);
        throw new Error(Object.values(e.response.data.errors).join(' '));
      }
      throw e;
    }
  };

  const send = async (expected) => {
    const r = (await axiosService.post('/admin/email/send/', { options, expected })).data;
    setToast(`Sent ${r.sent} email(s)${r.failed ? `, ${r.failed} failed: ${r.failed_emails.join(', ')}` : ''}.`);
    setReviewing(false);
  };

  const audience = meta?.audience || {};
  const recipientLabel = { all: `all active users (${audience.all ?? '…'})`, superusers: `superusers (${audience.superusers ?? '…'})`, test: testEmail };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <BackButton position="inline" />
        <CampaignIcon color="primary" />
        <Typography variant="h4" fontWeight={800}>Announcement emails</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Compose an announcement, then review it: the review window lists every email that will go out, who gets it,
        who is skipped and why, and shows each recipient's personal version. Each address gets one email. Nothing is
        sent until you confirm there.
      </Typography>

      <Typography variant="h6" fontWeight={700} mb={1}>1. Template</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 3 }}>
        {(meta?.templates || Object.keys(TEMPLATES).map((key) => ({ key, label: key }))).map(({ key, label }) => (
          <Card key={key} variant="outlined" sx={{ borderColor: template === key ? 'primary.main' : 'divider', borderWidth: template === key ? 2 : 1 }}>
            <CardActionArea onClick={() => pickTemplate(key)} sx={{ p: 1.75, height: '100%', alignItems: 'flex-start', display: 'block' }}>
              <Typography fontSize="1.5rem" lineHeight={1}>{TEMPLATES[key]?.icon}</Typography>
              <Typography fontWeight={700} mt={0.75}>{label}</Typography>
              <Typography variant="caption" color="text.secondary">{TEMPLATES[key]?.text}</Typography>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      <Typography variant="h6" fontWeight={700} mb={1}>2. Content</Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <TextField label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} fullWidth
                     error={!!errors.subject} helperText={errors.subject || 'Sent as “[Otable] …”'} />
          {template === 'bulk_email' && (
            <TextField label="Message (HTML allowed)" value={message} onChange={(e) => setMessage(e.target.value)}
                       multiline minRows={6} fullWidth error={!!errors.message} helperText={errors.message} />
          )}
          {template === 'ai_announcement' && (
            <TextField label="Free AI credits to advertise" type="number" value={aiCredits} sx={{ width: 260 }}
                       onChange={(e) => setAiCredits(Number(e.target.value))} inputProps={{ min: 1, max: 100 }} />
          )}
          {withCalls(template) && (
            <Box>
              <Typography variant="subtitle2" mb={1}>Funding calls</Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <ToggleButtonGroup size="small" exclusive value={callMode} onChange={(_, v) => v && setCallMode(v)}>
                  <ToggleButton value="profile">Match each profile</ToggleButton>
                  <ToggleButton value="auto">Top calls by budget</ToggleButton>
                  <ToggleButton value="search">Pick calls</ToggleButton>
                </ToggleButtonGroup>
                {callMode !== 'search' && (
                  <TextField select size="small" label="Calls per email" value={callLimit} sx={{ width: 150 }}
                             onChange={(e) => setCallLimit(Number(e.target.value))}>
                    {[1, 2, 3, 4, 5, 6, 7].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                  </TextField>
                )}
              </Stack>
              {callMode === 'profile' && (
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  Calls are matched to each recipient's skills and research interests. Users with no matching open call
                  are skipped for call announcements.
                </Typography>
              )}
              {callMode === 'search' && (
                <Autocomplete
                  multiple sx={{ mt: 2 }} options={callResults?.results || []} value={calls}
                  onChange={(_, v) => setCalls(v)} onInputChange={(_, v, reason) => reason === 'input' && setCallQuery(v)}
                  isOptionEqualToValue={(a, b) => a.id === b.id} filterOptions={(x) => x}
                  getOptionLabel={(c) => `${c.code || 'Call'} — ${c.title}`}
                  renderOption={(props, c) => (
                    <li {...props} key={c.id}>
                      <Box>
                        <Typography variant="body2"><strong>{c.code}</strong> {c.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{c.program} · closes {c.closes_at} {c.budget && `· ${c.budget}`}</Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Search open calls" error={!!errors.selected_call_ids} helperText={errors.selected_call_ids} />
                  )}
                />
              )}
            </Box>
          )}
        </Stack>
      </Paper>

      <Typography variant="h6" fontWeight={700} mb={1}>3. Recipients</Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <RadioGroup value={recipientType} onChange={(e) => setRecipientType(e.target.value)}>
          <FormControlLabel value="test" control={<Radio />} label="Test: one email to a single address, personalised as you" />
          {recipientType === 'test' && (
            <TextField size="small" label="Test address" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                       sx={{ ml: 4, mb: 1, maxWidth: 360 }} error={!!errors.test_email} helperText={errors.test_email} />
          )}
          <FormControlLabel value="superusers" control={<Radio />} label={<>Superusers <Chip size="small" label={audience.superusers ?? '…'} /></>} />
          <FormControlLabel value="all" control={<Radio />} label={<>All active users <Chip size="small" label={audience.all ?? '…'} /></>} />
        </RadioGroup>
        {recipientType !== 'test' && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Users who turned off email notifications are skipped. Send a test to yourself first.
          </Alert>
        )}
      </Paper>

      <Box display="flex" justifyContent="flex-end">
        <Button size="large" variant="contained" startIcon={<ReviewIcon />} onClick={() => { setErrors({}); setReviewing(true); }}
                disabled={!subject.trim()}>
          Review emails
        </Button>
      </Box>

      <EmailOutboxDialog
        open={reviewing}
        onClose={() => setReviewing(false)}
        title={`${meta?.templates?.find((t) => t.key === template)?.label || 'Announcement'} → ${recipientLabel[recipientType]}`}
        description={`Subject: [Otable] ${subject}`}
        csvName={`${template}-${recipientType}.csv`}
        loadOutbox={loadOutbox}
        loadPreview={async (email) => (await axiosService.post('/admin/email/preview/', { options, key: email.key }, {
          responseType: 'text', transformResponse: (r) => r,
        })).data}
        onSend={send}
      />
      {!meta && <Box display="flex" justifyContent="center" mt={2}><CircularProgress size={20} /></Box>}
      <Snackbar open={!!toast} autoHideDuration={8000} onClose={() => setToast(null)} message={toast} />
    </Container>
  );
}
